import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { CategoriaProductoApiService } from '../services/categoria-producto-api.service';
import {
  ProductCategoryResponse,
  ProductCategoryRequest,
} from '../models/categoria-producto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-categoria-producto-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
  ],
  templateUrl: './categoria-producto-form.component.html',
})
export class CategoriaProductoFormComponent implements OnInit {
  @Input() drawerMode = false;
  @Output() saved = new EventEmitter<ProductCategoryResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  categoryId: number | null = null;

  categorias: ProductCategoryResponse[] = [];
  loadingCategorias = false;

  public readonly MAX_DEPTH = 3;

  constructor(
    private fb: FormBuilder,
    private categoriaApi: CategoriaProductoApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.cargarCategorias();

    if (!this.drawerMode) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.isEditing = true;
        this.categoryId = +id;
        this.cargarCategoria(this.categoryId);
      }
    }
  }

  reset(): void {
    this.isEditing = false;
    this.categoryId = null;
    this.error = '';
    this.saving = false;
    this.initForm();
  }

  loadCategoriaProducto(id: number): void {
    this.isEditing = true;
    this.categoryId = id;
    this.cargarCategoria(id);
  }

  private cargarCategorias(): void {
    this.loadingCategorias = true;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.categoriaApi
      .searchProductCategory({ businessId, activeOnly: true })
      .subscribe({
        next: (data) => {
          this.categorias = data;
          this.loadingCategorias = false;
        },
        error: () => {
          this.loadingCategorias = false;
        },
      });
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', Validators.maxLength(300)],
      type: ['both', Validators.required],
      icon: ['', Validators.maxLength(50)],
      order: [1, [Validators.required, Validators.min(1)]],
      idParent: [null],
      isActive: [true],
    });
  }

  private cargarCategoria(id: number): void {
    this.loading = true;

    this.categoriaApi.getProductCategoryById(id).subscribe({
      next: (category: ProductCategoryResponse) => {
        this.form.patchValue({
          name: category.name,
          description: category.description || '',
          type: category.type,
          icon: category.icon || '',
          order: category.order,
          idParent: category.idParent || null,
          isActive: category.isActive,
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la categoría';
        this.loading = false;
      },
    });
  }

  // sub categorias

  private getCategoriaDepth(categoryId: number): number {
    let depth = 1;
    let currentId: number | null | undefined = categoryId;
    const seen = new Set<number>();

    while (currentId) {
      if (seen.has(currentId)) break; // evita ciclos infinitos
      seen.add(currentId);
      const current = this.categorias.find((c) => c.id === currentId);
      if (!current || !current.idParent) break;
      depth++;
      currentId = current.idParent;
    }
    return depth;
  }

  private getDescendientesIds(categoryId: number): Set<number> {
    const descendientes = new Set<number>();
    const queue: number[] = [categoryId];

    while (queue.length > 0) {
      const id = queue.shift()!;
      const hijos = this.categorias.filter((c) => c.idParent === id);
      for (const h of hijos) {
        if (!descendientes.has(h.id)) {
          descendientes.add(h.id);
          queue.push(h.id);
        }
      }
    }
    return descendientes;
  }

  get categoriasForParentSelect(): Array<
    ProductCategoryResponse & {
      _depth: number;
      _label: string;
      _disabled: boolean;
    }
  > {
    const excluidas = new Set<number>();

    if (this.isEditing && this.categoryId) {
      excluidas.add(this.categoryId);
      this.getDescendientesIds(this.categoryId).forEach((id) =>
        excluidas.add(id),
      );
    }

    const result: Array<
      ProductCategoryResponse & {
        _depth: number;
        _label: string;
        _disabled: boolean;
      }
    > = [];

    // Recorrido en orden de árbol (cada hija justo debajo de su padre)
    const agregarNodos = (parentId: number | null, depth: number): void => {
      const nodos = this.categorias
        .filter(
          (c) => (c.idParent ?? null) === parentId && !excluidas.has(c.id),
        )
        .sort(
          (a, b) =>
            (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name),
        );

      for (const nodo of nodos) {
        const tieneHijos = this.categorias.some((c) => c.idParent === nodo.id);
        const disabled = depth >= this.MAX_DEPTH;

        // Indentación con espacios duros + conector de árbol
        const sangria =
          depth > 1 ? '\u00A0\u00A0\u00A0\u00A0'.repeat(depth - 1) + '└ ' : '';
        const icono = tieneHijos ? '📁' : '📄';
        const sufijo = disabled ? '  —  nivel máximo' : '';

        result.push({
          ...nodo,
          _depth: depth,
          _disabled: disabled,
          _label: `${sangria}${icono} ${nodo.name}${sufijo}`,
        });

        agregarNodos(nodo.id, depth + 1);
      }
    };

    agregarNodos(null, 1);
    return result;
  }

  // ¿Existe al menos una categoría que sí pueda ser padre?
  get haySeleccionablesComoPadre(): boolean {
    return this.categoriasForParentSelect.some((c) => !c._disabled);
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Categoría' : 'Nueva Categoría';
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.form.pristine) {
      this.toast.info({
        title: 'Categoría',
        message: 'No se realizaron nuevos cambios en la categoría',
      });
      this.router.navigate(['/categorias-producto']);
      return;
    }

    const idParent = this.form.value.idParent;
    if (idParent) {
      const profundidadPadre = this.getCategoriaDepth(idParent);
      if (profundidadPadre >= this.MAX_DEPTH) {
        this.error = `No puedes crear más niveles. El límite máximo es ${this.MAX_DEPTH} niveles jerárquicos.`;
        return;
      }
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const categoryRequest: ProductCategoryRequest = {
      name: this.form.value.name,
      description: this.form.value.description || undefined,
      type: this.form.value.type,
      icon: this.form.value.icon || undefined,
      order: this.form.value.order,
      idParent: this.form.value.idParent || undefined,
      businessId,
      isActive: this.form.value.isActive,
    };

    const request$ =
      this.isEditing && this.categoryId
        ? this.categoriaApi.updateProductCategory(
            this.categoryId,
            categoryRequest,
          )
        : this.categoriaApi.createProductCategory(categoryRequest);

    request$.subscribe({
      next: (saved) => {
        this.saving = false;
        if (this.drawerMode) {
          this.saved.emit(saved);
        } else {
          this.router.navigate(['/categorias-producto']);
        }
      },
      error: (err) => {
        this.saving = false;
        this.error =
          err.error?.message ||
          (this.isEditing
            ? 'Error al actualizar la categoría'
            : 'Error al crear la categoría');
      },
    });
  }

  cancelar(): void {
    if (this.drawerMode) {
      this.cancelled.emit();
    } else {
      this.router.navigate(['/categorias-producto']);
    }
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return control ? control.invalid && control.touched : false;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['maxlength'])
      return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;

    return 'Campo inválido';
  }
}
